import { Controller } from "@hotwired/stimulus"
import {
  MAX_UNIT_QUANTITY, fixGroup, canIncrement, canDecrement, incrementMax, decrementMax, setRepeat, maxLabel, groupChip,
  ruleSentence, unitLabel, unitSubtitle, unitPreviewText, optionsError, toBackendMax, fromBackendMax
} from "../modules/product_options.mjs"
import { wizardMobile } from "../modules/wizard_layout.js"

// Paso Opciones del wizard: modo, unidades, grupos por unidad y biblioteca de grupos. Reglas en
// product_options.mjs; estado publicado en data-options-*; elementos internos por data-role.
// Doc: _docs/features/products/partner_product_form.md

export default class extends Controller {
  static targets = ["mode", "note", "block", "listTitle", "orderToggle", "units", "unit", "unitTemplate", "groupTemplate", "splitCard", "addUnit", "unsplit"]
  static values = { split: Boolean }

  connect() {
    this.onKeydown = event => { if (event.key === "Escape") this.closeOverlay() }
    this.onDocumentClick = event => {
      if (this.overlay?.dismissOutside && !this.overlay.panel.contains(event.target) && !this.overlay.opener?.contains(event.target)) this.closeOverlay()
    }
    this.beforeCache = () => this.closeOverlay()
    document.addEventListener("turbo:before-cache", this.beforeCache)
    // Claves numéricas únicas para unidades y grupos nuevos
    this.rowSequence = 0
    // splitValue viene del servidor (Product#split_units?); acá solo se enciende
    this.modeChanged()
  }

  // Borrador local: modo, división y unidades con sus grupos por id
  get draft() {
    return {
      hasOptions: this.hasOptions,
      split: this.splitValue,
      units: this.liveUnits().map(unit => ({
        ...this.#unitData(unit),
        groups: this.liveGroups(unit).map(group => ({
          categoryId: Number(group.dataset.categoryId),
          max: Number(group.querySelector("[data-role='max-input']").value),
          required: group.querySelector("[data-role='required-input']").value === "1",
          repeat: group.querySelector("[data-role='repeat-input']").value === "1",
          alternativeIds: [...group.querySelectorAll("[data-role='alt']")].filter(alt => alt.checked).map(alt => Number(alt.value))
        }))
      }))
    }
  }

  // Reconstruye el paso desde un borrador; lo que ya no existe en la tienda se omite y se cuenta
  restoreDraft(options) {
    if (!options || options.hasOptions === null || options.hasOptions === undefined) return { skipped: 0 }
    let skipped = 0
    this.closeOverlay()
    this.modeTargets.forEach(radio => { radio.checked = radio.value === (options.hasOptions ? "1" : "0") })
    this.liveUnits().filter(unit => !unit.querySelector("[data-role='unit-id']")).forEach(unit => unit.remove())
    if (options.hasOptions) {
      ;(options.units || []).forEach(data => {
        const unit = this.#appendUnit()
        const name = unit.querySelector("[data-role='unit-name']")
        const quantity = unit.querySelector("[data-role='unit-qty']")
        if (name) name.value = data.name || ""
        if (quantity) {
          quantity.value = Math.min(MAX_UNIT_QUANTITY, Math.max(1, Number(data.quantity) || 1))
          quantity.dispatchEvent(new Event("input", { bubbles: true }))
        }
        ;(data.groups || []).forEach(groupData => {
          const group = this.#insertGroup(unit, String(groupData.categoryId))
          if (!group) { skipped += 1; return }
          group.querySelector("[data-role='max-input']").value = groupData.max ?? -1
          group.querySelector("[data-role='required-input']").value = groupData.required ? "1" : "0"
          group.querySelector("[data-role='repeat-input']").value = groupData.repeat ? "1" : "0"
          const wanted = (groupData.alternativeIds || []).map(Number)
          const alts = [...group.querySelectorAll("[data-role='alt']")]
          alts.forEach(alt => { alt.checked = wanted.includes(Number(alt.value)) })
          skipped += wanted.length - alts.filter(alt => alt.checked).length
        })
      })
      this.splitValue = Boolean(options.split)
    }
    this.modeChanged()
    return { skipped }
  }

  disconnect() {
    this.closeOverlay()
    document.removeEventListener("turbo:before-cache", this.beforeCache)
  }

  get hasOptions() {
    const checked = this.modeTargets.find(radio => radio.checked)
    return checked ? checked.value === "1" : null
  }

  // ---------- modo ----------

  // "Tal cual" deshabilita el bloque completo (no viaja ninguna unidad)
  modeChanged() {
    const has = this.hasOptions
    this.blockTarget.hidden = has !== true
    this.blockTarget.disabled = has !== true
    this.noteTarget.hidden = has !== false
    if (has === true && this.liveUnits().length === 0) this.#appendUnit()
    this.refresh()
  }

  // ---------- unidades ----------

  splitUnits() {
    this.splitValue = true
    this.refresh()
    if (this.#mobile) this.#openUnitEditor(this.liveUnits()[0])
  }

  addUnit() {
    const unit = this.#appendUnit()
    this.splitValue = true
    this.refresh()
    if (this.#mobile) this.#openUnitEditor(unit)
    else unit.querySelector("[data-role='unit-name']")?.focus()
  }

  // Deshace "Dividir por unidades" (solo con una unidad): sin nombre, cantidad 1, conserva sus grupos
  unsplitUnits() {
    const units = this.liveUnits()
    if (units.length !== 1) return
    this.closeOverlay()
    this.splitValue = false
    const name = units[0].querySelector("[data-role='unit-name']")
    const quantity = units[0].querySelector("[data-role='unit-qty']")
    if (name) name.value = ""
    if (quantity) {
      quantity.value = 1
      quantity.dispatchEvent(new Event("input", { bubbles: true }))
    }
    this.refresh()
  }

  openUnit(event) {
    this.#openUnitEditor(event.currentTarget.closest("[data-role='unit']"))
  }

  closeUnit() {
    this.closeOverlay()
  }

  // Una unidad guardada se marca para destruir y se oculta; una nueva se quita del DOM
  removeUnit(event) {
    if (this.liveUnits().length <= 1) return
    const unit = event.currentTarget.closest("[data-role='unit']")
    this.closeOverlay()
    if (unit.querySelector("input[name$='[id]'][data-role='unit-id']")) {
      unit.querySelector("[data-role='unit-destroy']").value = "1"
      unit.hidden = true
    } else {
      unit.remove()
    }
    this.refresh()
  }

  unitChanged() {
    this.refresh()
  }

  // ---------- biblioteca de grupos ----------

  openLibrary(event) {
    const unit = event.currentTarget.closest("[data-role='unit']")
    this.#openOverlay(unit.querySelector("[data-role='unit-library']"), { opener: event.currentTarget, dismissOutside: !this.#mobile })
  }

  closeLibrary() {
    this.closeOverlay()
  }

  // El grupo entra con todas sus alternativas, obligatorio y máximo 1, con clave numérica propia
  addGroup(event) {
    const row = event.currentTarget
    const group = this.#insertGroup(row.closest("[data-role='unit']"), row.dataset.categoryId)
    if (!group) return
    this.closeOverlay()
    this.refresh()
    this.#openGroupEditor(group)
  }

  // ---------- grupos ----------

  openGroup(event) {
    if (this.element.classList.contains("is-ordering")) return
    const group = event.currentTarget.closest("[data-role='group']")
    if (!this.#mobile && group.classList.contains("is-open")) return this.#closeAccordion(group)
    this.#openGroupEditor(group)
  }

  closeGroup(event) {
    const group = event.currentTarget.closest("[data-role='group']")
    if (this.#mobile) this.closeOverlay()
    else this.#closeAccordion(group)
  }

  removeGroup(event) {
    const group = event.currentTarget.closest("[data-role='group']")
    this.closeOverlay()
    if (group.querySelector("input[name$='[id]']")) {
      group.querySelector("[data-role='destroy-input']").value = "1"
      group.querySelectorAll("[data-role='alt']").forEach(alt => { alt.checked = false; alt.disabled = true })
      group.classList.remove("is-open")
      group.hidden = true
    } else {
      group.remove()
    }
    this.refresh()
  }

  moveUp(event) {
    this.#move(event.currentTarget.closest("[data-role='group']"), -1)
  }

  moveDown(event) {
    this.#move(event.currentTarget.closest("[data-role='group']"), 1)
  }

  toggleOrder() {
    const ordering = this.element.classList.toggle("is-ordering")
    this.orderToggleTarget.textContent = ordering ? "Listo" : "Ordenar"
  }

  // Cambiar alternativas normaliza la regla como cualquier cambio del partner
  toggleAll(event) {
    const group = event.currentTarget.closest("[data-role='group']")
    const alts = [...group.querySelectorAll("[data-role='alt']")]
    const all = alts.every(alt => alt.checked)
    alts.forEach(alt => { alt.checked = !all })
    this.#updateGroup(group, g => g)
  }

  setRequired(event) {
    const group = event.currentTarget.closest("[data-role='group']")
    group.querySelector("[data-role='required-input']").value = event.currentTarget.dataset.value
    this.refresh()
  }

  maxDecrement(event) {
    this.#updateGroup(event.currentTarget.closest("[data-role='group']"), decrementMax)
  }

  maxIncrement(event) {
    this.#updateGroup(event.currentTarget.closest("[data-role='group']"), incrementMax)
  }

  setRepeat(event) {
    const repeat = event.currentTarget.dataset.value === "1"
    this.#updateGroup(event.currentTarget.closest("[data-role='group']"), g => setRepeat(g, repeat))
  }

  groupChanged(event) {
    this.#updateGroup(event.currentTarget.closest("[data-role='group']"), g => g)
  }

  closeOverlay() {
    if (!this.overlay) return
    const { panel, opener } = this.overlay
    panel.classList.remove("is-open")
    this.overlay = null
    this.element.classList.remove("has-overlay")
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("click", this.onDocumentClick, true)
    opener?.focus?.()
  }

  // ---------- estado ----------

  liveUnits() {
    return this.unitTargets.filter(unit => !unit.hidden)
  }

  liveGroups(unit) {
    return [...unit.querySelectorAll("[data-role='group']")].filter(group => !group.hidden)
  }

  // Renumera, pinta y publica el estado y el aviso del paso
  refresh() {
    const split = this.splitValue
    this.element.classList.toggle("is-split", split)
    const units = this.liveUnits()
    let position = 0
    const state = units.map((unit, unitIndex) => {
      const data = this.#unitData(unit)
      const label = unitLabel(data.name, unitIndex)
      this.#paintUnit(unit, { label, unitIndex, data, removable: units.length > 1, split })
      const groups = this.liveGroups(unit)
      groups.forEach((group, index) => {
        position += 1
        group.querySelector("[data-role='position-input']").value = position
        this.#paintGroup(group, { number: index + 1 })
      })
      this.#paintLibrary(unit, groups)
      return { label, quantity: data.quantity, groups: groups.map(group => this.#groupState(group)) }
    })
    this.listTitleTarget.textContent = split ? "Lo que incluye el combo" : "Grupos de opciones"
    this.splitCardTarget.hidden = split
    this.addUnitTarget.hidden = !split
    if (this.hasUnsplitTarget) this.unsplitTarget.hidden = !(split && units.length === 1)
    const totalGroups = state.reduce((sum, unit) => sum + unit.groups.length, 0)
    this.orderToggleTarget.hidden = totalGroups < 2
    if (totalGroups < 2 && this.element.classList.contains("is-ordering")) this.toggleOrder()
    const has = this.hasOptions
    const error = has === true
      ? optionsError(state.map(unit => ({ label: unit.label, groups: unit.groups.map(group => ({ name: group.name, selected: group.alternatives.length })) })), split)
      : ""
    this.element.dataset.optionsError = error
    this.element.dataset.optionsState = JSON.stringify({ hasOptions: has, split, units: state })
    this.dispatch("change")
  }

  #unitData(unit) {
    const quantity = Number(unit.querySelector("[data-role='unit-qty']")?.value) || 1
    return { name: unit.querySelector("[data-role='unit-name']")?.value || "", quantity }
  }

  #paintUnit(unit, { label, unitIndex, data, removable, split }) {
    const text = (role, value) => { const el = unit.querySelector(`[data-role='${role}']`); if (el && el.textContent !== value) el.textContent = value }
    text("unit-kicker", `Unidad ${unitIndex + 1}`)
    text("unit-label", label)
    text("unit-pill", `× ${data.quantity}`)
    text("unit-sub", unitSubtitle(data.name, data.quantity, unitIndex))
    text("unit-hint", unitSubtitle(data.name, data.quantity, unitIndex))
    text("unit-preview", unitPreviewText(data.name, data.quantity, unitIndex))
    text("unit-add-label", split ? `Agregar grupo a ${label}` : "Agregar grupo de opciones")
    text("library-title", split ? `Agregar grupo a ${label}` : "Agregar grupo de opciones")
    unit.querySelectorAll("[data-role='unit-remove'], [data-role='unit-remove-x']").forEach(button => { button.hidden = !removable })
  }

  #group(group) {
    const alts = [...group.querySelectorAll("[data-role='alt']")]
    return {
      selected: alts.filter(alt => alt.checked).length,
      total: alts.length,
      max: fromBackendMax(group.querySelector("[data-role='max-input']").value),
      required: group.querySelector("[data-role='required-input']").value === "1",
      repeat: group.querySelector("[data-role='repeat-input']").value === "1",
      priced: alts.some(alt => alt.checked && Number(alt.dataset.price) > 0),
      picked: alts.filter(alt => alt.checked).map(alt => alt.dataset.name)
    }
  }

  #writeGroup(group, g) {
    group.querySelector("[data-role='max-input']").value = toBackendMax(g.max)
    group.querySelector("[data-role='repeat-input']").value = g.repeat ? "1" : "0"
  }

  // Único camino que escribe los hidden del grupo (normalizado con fixGroup)
  #updateGroup(group, change) {
    this.#writeGroup(group, change(fixGroup(this.#group(group))))
    this.refresh()
  }

  // Pinta desde los hidden sin normalizar: una regla guardada que la UI no produce no se reescribe
  #paintGroup(group, { number }) {
    const g = this.#group(group)
    const text = (role, value) => { const el = group.querySelector(`[data-role='${role}']`); if (el && el.textContent !== value) el.textContent = value }
    const incomplete = g.selected === 0
    text("group-number", String(number))
    text("group-kicker", `Grupo ${number} · ${group.closest("[data-role='unit']")?.querySelector("[data-role='unit-label']")?.textContent || ""}`)
    text("group-rule", incomplete ? "Falta elegir alternativas" : groupChip(g))
    text("group-picked", g.picked.join(", "))
    text("alt-count", `${g.selected} de ${g.total} elegidas`)
    text("alt-toggle", g.selected === g.total && g.total > 0 ? "Quitar todas" : "Elegir todas")
    text("max-display", maxLabel(g.max))
    text("sentence-text", ruleSentence(g, { priced: g.priced }))
    group.classList.toggle("is-incomplete", incomplete)
    group.classList.toggle("is-repeat", g.repeat)
    group.querySelectorAll("[data-role='required-btn']").forEach(button => button.setAttribute("aria-pressed", (button.dataset.value === "1") === g.required))
    group.querySelectorAll("[data-role='how-btn']").forEach(button => button.setAttribute("aria-pressed", (button.dataset.value === "1") === g.repeat))
    const howBlock = group.querySelector("[data-role='how-block']")
    if (howBlock) howBlock.hidden = g.max === 1
    const dec = group.querySelector("[data-role='max-dec']")
    const inc = group.querySelector("[data-role='max-inc']")
    if (dec) dec.disabled = !canDecrement(g)
    if (inc) inc.disabled = !canIncrement(g)
  }

  #groupState(group) {
    const g = this.#group(group)
    const alts = [...group.querySelectorAll("[data-role='alt']")].filter(alt => alt.checked)
    return {
      name: group.dataset.groupVisible || group.dataset.groupName,
      internal: group.dataset.groupName,
      required: g.required, max: g.max, repeat: g.repeat,
      alternatives: alts.map(alt => ({ name: alt.dataset.name, price: Number(alt.dataset.price) || 0 }))
    }
  }

  #paintLibrary(unit, groups) {
    const added = new Set(groups.map(group => group.dataset.categoryId))
    unit.querySelectorAll("[data-role='library-row']").forEach(row => {
      const used = added.has(row.dataset.categoryId)
      row.classList.toggle("is-added", used)
      row.setAttribute("aria-disabled", used)
    })
  }

  #appendUnit() {
    this.unitsTarget.insertAdjacentHTML("beforeend", this.unitTemplateTarget.innerHTML.replace(/NEW_RECORD/g, this.#rowKey()))
    return this.unitsTarget.lastElementChild
  }

  // Clona el template del grupo dentro de la unidad; null si ya está o la tienda ya no lo tiene
  #insertGroup(unit, categoryId) {
    if (!unit || this.liveGroups(unit).some(group => group.dataset.categoryId === categoryId)) return null
    const template = this.groupTemplateTargets.find(item => item.dataset.categoryId === categoryId)
    if (!template) return null
    const list = unit.querySelector("[data-role='unit-groups']")
    list.insertAdjacentHTML("beforeend", template.innerHTML.replace(/UNIT_INDEX/g, unit.dataset.unitIndex).replace(/GROUP_INDEX/g, this.#rowKey()))
    return list.lastElementChild
  }

  // Rails solo acepta claves numéricas en las colecciones anidadas
  #rowKey() {
    return `${Date.now()}${this.rowSequence++}`
  }

  #move(group, direction) {
    const unit = group.closest("[data-role='unit']")
    const groups = this.liveGroups(unit)
    const index = groups.indexOf(group)
    const target = groups[index + direction]
    if (!target) return
    direction < 0 ? target.before(group) : target.after(group)
    this.refresh()
    group.querySelector(direction < 0 ? "[data-action*='moveUp']" : "[data-action*='moveDown']")?.focus()
  }

  #openUnitEditor(unit) {
    if (!unit) return
    this.#openOverlay(unit.querySelector("[data-role='unit-editor']"), { opener: unit.querySelector("[data-action*='openUnit']") })
  }

  // Mobile: vista a pantalla completa; desktop: acordeón (uno abierto a la vez)
  #openGroupEditor(group) {
    const editor = group.querySelector("[data-role='group-editor']")
    if (this.#mobile) {
      this.#openOverlay(editor, { opener: group.querySelector("[data-action*='openGroup']") })
      return
    }
    this.unitTargets.forEach(unit => this.liveGroups(unit).forEach(other => { if (other !== group) this.#closeAccordion(other) }))
    group.classList.add("is-open")
    group.querySelector("[data-action*='openGroup']")?.setAttribute("aria-expanded", "true")
  }

  #closeAccordion(group) {
    group.classList.remove("is-open")
    group.querySelector("[data-action*='openGroup']")?.setAttribute("aria-expanded", "false")
  }

  #openOverlay(panel, { opener = null, dismissOutside = false } = {}) {
    if (!panel) return
    this.closeOverlay()
    panel.classList.add("is-open")
    // Solo los sheets y el editor a pantalla completa (mobile) bloquean el scroll del body
    this.element.classList.toggle("has-overlay", this.#mobile)
    this.overlay = { panel, opener, dismissOutside }
    document.addEventListener("keydown", this.onKeydown)
    if (dismissOutside) document.addEventListener("click", this.onDocumentClick, true)
    const focusable = panel.querySelector("input:not([type='hidden']):not([disabled]), button:not([disabled]):not([aria-disabled='true'])")
    focusable?.focus({ preventScroll: true })
  }

  get #mobile() {
    return wizardMobile(this.element)
  }
}
